import {
  FocusEventHandler,
  InputHTMLAttributes,
  FocusEvent,
  createRef,
  forwardRef,
  useEffect,
  useState,
  KeyboardEvent,
  ChangeEvent,
  FC,
  SVGProps,
  useCallback,
  useRef,
  ReactElement,
  MouseEventHandler,
  KeyboardEventHandler,
  ReactNode,
} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { ulid } from 'ulid';
import cx from 'classnames';
import { ValidationError } from 'class-validator';
import { ReactComponent as Down } from '../assets/icons/chevron_down.svg';
import groupStyles from './InputGroup.module.scss';
import styles from './FormInput.module.scss';
import { onEnter } from '../lib/onEnter';
import { useAmplitude } from '../amplitude/amplitudeContext';

export interface DropdownOptionProps {
  label: string | ReactNode;
  value: string;
  onClick?: MouseEventHandler;
  onFocus?: FocusEventHandler;
  onKeyDown?: KeyboardEventHandler;
  onMouseEnter?: MouseEventHandler;
  /** Suggestion that appears in the dropdown option
   * UNSTABLE
   */
  optionIconLeft?: ReactElement;
  /** Suggestion that appears in the dropdown option */
  optionIconRight?: ReactElement;
  selected?: boolean;
}

export interface FormInputProps {
  className?: string;
  clearButton?: boolean;
  /** Suggestion dropdown should auto select the first option */
  defaultToFirstOption?: boolean;
  /** Input should be treated as a static dropdown whose value can only be one of the options */
  dropdown?: boolean;
  /** Suggestion that appears in all dropdown options */
  optionIconLeft?: ReactElement;
  optionIconRight?: ReactElement;
  error?: ValidationError | null;
  icon?: FC<SVGProps<SVGSVGElement>>;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  id?: string;
  label?: string;
  multiline?: boolean;
  onBlur?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onChange?: (value: string) => void;
  onFocus?: FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  placeholder?: string;
  options?: Array<DropdownOptionProps>;
  required?: boolean;
  value?: string;
}

// https://github.com/yannickcr/eslint-plugin-react/issues/3015
/* eslint-disable react/prop-types */
const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      className,
      clearButton,
      defaultToFirstOption,
      dropdown,
      error,
      icon: Icon,
      inputMode,
      id,
      label,
      multiline,
      onBlur,
      onChange,
      onFocus,
      optionIconLeft,
      optionIconRight,
      placeholder,
      options = [],
      required,
      value,
      ...props
      /* eslint-enable react/prop-types */
    },
    ref
  ) => {
    const [realId, setRealId] = useState(id || /* istanbul ignore next */ '');
    useEffect(() => {
      setRealId(id || /* istanbul ignore next */ `input-${ulid()}`);
    }, [id]);

    const [realLabel, setRealLabel] = useState(label || /* istanbul ignore next */ '');
    useEffect(() => {
      setRealLabel(required ? `${label}*` : label || /* istanbul ignore next */ '');
    }, [label, required]);

    const [realInputMode, setRealInputMode] = useState<FormInputProps['inputMode']>('search');
    useEffect(() => {
      setRealInputMode(inputMode);
    }, [inputMode]);

    const [focused, setFocused] = useState(false);
    const blur = useCallback(
      (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFocused(false);
        setShowDropdown(false);
        setRealInputMode('none');
        if (onBlur) {
          onBlur(e);
        }
      },
      [onBlur]
    );
    const focus = useCallback(
      (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setShowDropdown(true);
        setFocused(true);
        setRealInputMode(inputMode || 'text');
        if (onFocus) {
          onFocus(e);
        }
      },
      [inputMode, onFocus]
    );
    const change = useCallback(
      (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (onChange && typeof onChange === 'function') {
          onChange(e.currentTarget.value);
        }
      },
      [onChange]
    );

    const { logAmplitudeEvent } = useAmplitude();

    const [showDropdown, setShowDropdown] = useState(false);
    const buildSuggestion = useCallback(
      (suggestion: string) => {
        if (suggestion) {
          if (placeholder === 'Location') {
            logAmplitudeEvent('selects a suggestion in the Location search box');
          }
          if (placeholder === 'Name') {
            logAmplitudeEvent('selects a suggestion in the Name search box');
          }
        }
        setRealInputMode('none');
        setShowDropdown(false);
        if (onChange) {
          onChange(suggestion);
        }
      },
      [logAmplitudeEvent, onChange, placeholder]
    );

    const [suggestedIndex, setSuggestedIndex] = useState(defaultToFirstOption ? 0 : -1);
    const floaterRef = useRef<HTMLDivElement>(null);
    const optionRefs = useRef(options?.map(() => createRef<HTMLDivElement>()));
    const [isScrolling, setIsScrolling] = useState(false);
    const keyboardNavigation = useCallback(
      (e: KeyboardEvent<HTMLInputElement>) => {
        if (options?.length) {
          if (e.key.startsWith('Arrow')) {
            setIsScrolling(false);
          }
          if (e.key === 'ArrowDown' && suggestedIndex < options.length - 1) {
            setSuggestedIndex((s) => s + 1);
          }
          if (e.key === 'ArrowUp' && suggestedIndex > -1) {
            setSuggestedIndex((s) => s - 1);
          }
          if (e.key === 'Enter') {
            setShowDropdown(false);
            setRealInputMode(inputMode || 'text');

            if (suggestedIndex > -1) {
              e.preventDefault();
              const option = options[suggestedIndex];
              buildSuggestion(option.value);
            }
          }
          if (e.key !== 'Enter') {
            setShowDropdown(true);
          }
        }
      },
      [buildSuggestion, inputMode, options, suggestedIndex]
    );
    useEffect(() => {
      setSuggestedIndex(defaultToFirstOption ? 0 : -1);
      optionRefs.current = options?.map(() => createRef<HTMLDivElement>());
    }, [options, defaultToFirstOption]);
    useEffect(() => {
      if (!isScrolling && floaterRef.current && optionRefs.current && suggestedIndex > -1) {
        const opt = optionRefs.current[suggestedIndex].current;

        if (opt) {
          const { offsetHeight: optionHeight, offsetTop } = opt;
          const { offsetHeight } = floaterRef.current;

          floaterRef.current.scrollTo({
            top: offsetTop - offsetHeight / 2 + optionHeight / 2,
            behavior: 'smooth',
          });
        }
      }
    }, [optionRefs, suggestedIndex, isScrolling]);

    const getOptionLabel = useCallback((): ReactNode | null => {
      if (!value) {
        return null;
      }

      const selectedOption = options.find((o) => {
        if (typeof o === 'string' && o === value) {
          return true;
        }

        if (typeof o !== 'string' && o.value === value) {
          return true;
        }

        return false;
      });

      if (!selectedOption) {
        return null;
      }

      return selectedOption.label;
    }, [options, value]);

    const [showError, setShowError] = useState(!!error);
    useEffect(() => {
      setShowError(!!error);
    }, [error]);
    useEffect(() => {
      setShowError(false);
    }, [value]);

    return (
      <label htmlFor={realId} className={cx(className, styles.wrapper, { [styles.focused]: focused, [styles.isDropdown]: dropdown, [styles.error]: showError })}>
        {realLabel && <div className={styles.label}>{realLabel}</div>}
        <div className={cx(styles.inputWrapper, groupStyles.input)}>
          {Icon && <Icon className={styles.icon} />}
          {multiline ? (
            <textarea {...props} className={cx(styles.input, styles.textarea)} id={realId} placeholder={placeholder} onBlur={blur} onChange={change} onFocus={focus} value={value} />
          ) : (
            <>
              <input
                {...props}
                aria-label="Search locations"
                className={cx(styles.input, { [styles.hidden]: dropdown })}
                id={realId}
                inputMode={realInputMode}
                placeholder={placeholder}
                onBlur={blur}
                onChange={change}
                onFocus={focus}
                onTouchEnd={() => setRealInputMode(inputMode || 'text')}
                onKeyDown={keyboardNavigation}
                ref={ref}
                value={value}
              />
              {dropdown && (
                <>
                  <div className={cx(styles.input, { [styles.placeholder]: !value })}>{getOptionLabel() || placeholder}</div>
                  <Down className={cx(styles.icon, styles.arrowIcon, { [styles.opened]: showDropdown })} onClick={() => setShowDropdown((s) => !s)} onMouseDown={(e) => e.preventDefault()} />
                </>
              )}
              {clearButton ? (
                <FontAwesomeIcon
                  onClick={() => {
                    buildSuggestion('');
                  }}
                  className={cx(styles.close, styles.icon, { [styles.hidden]: !value })}
                  icon={faTimes}
                />
              ) : null}
              {options?.length && showDropdown ? (
                <div
                  onMouseDown={(e) => e.preventDefault()}
                  className={styles.floater}
                  ref={floaterRef}
                  role="listbox"
                  tabIndex={0}
                  data-test="input-dropdown-floater"
                  onScroll={() => setIsScrolling(true)}
                >
                  {options.map((suggestion, i) => (
                    <DropdownOption
                      {...suggestion}
                      key={suggestion.value}
                      onClick={() => buildSuggestion(suggestion.value)}
                      onFocus={() => setSuggestedIndex(i)}
                      onMouseEnter={() => setSuggestedIndex(i)}
                      onKeyDown={onEnter(() => buildSuggestion(suggestion.value))}
                      optionIconLeft={optionIconLeft}
                      optionIconRight={optionIconRight}
                      ref={optionRefs.current && optionRefs.current[i]}
                      selected={suggestedIndex === i}
                    />
                  ))}
                </div>
              ) : null}
            </>
          )}
          <FontAwesomeIcon className={cx(styles.errorIcon, styles.icon, { [styles.visible]: showError && !focused })} icon={faExclamationTriangle} />
        </div>
        <div data-test="form-error" className={cx(styles.error, { [styles.visible]: showError })}>
          {/* eslint-disable-next-line react/prop-types */}
          {error?.constraints && Object.values(error.constraints)}
        </div>
      </label>
    );
  }
);

export const DropdownOption = forwardRef<
  HTMLDivElement,
  { onClick?: MouseEventHandler; onFocus?: FocusEventHandler; onKeyDown?: KeyboardEventHandler; onMouseEnter?: MouseEventHandler; selected?: boolean } & DropdownOptionProps
>(
  // eslint-disable-next-line react/prop-types
  ({ selected, label, onClick, onFocus, onKeyDown, onMouseEnter, optionIconLeft, optionIconRight }, ref) => (
    <div
      aria-selected={selected}
      className={cx(styles.option, {
        [styles.active]: selected,
        [styles.hasPill]: optionIconRight,
        [styles.center]: typeof label !== 'string',
      })}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onKeyDown={onKeyDown}
      ref={ref}
      role="option"
      tabIndex={0}
      data-test="input-dropdown-option"
    >
      {optionIconLeft && <div className={styles.optionIcon}>{optionIconLeft}</div>}
      {label && <span>{label}</span>}
      {optionIconRight && <div className={styles.optionIcon}>{optionIconRight}</div>}
    </div>
  )
);

export default FormInput;
